import { useState } from "react";
import {
  Button,
  Card,
  Group,
  Text,
  SimpleGrid,
  Badge,
  Stack,
  ActionIcon,
  Menu,
  Skeleton,
  Box,
} from "@mantine/core";
import {
  IconPlus,
  IconDotsVertical,
  IconEdit,
  IconTrash,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { modals } from "@mantine/modals";
import { tollLocationsApi } from "../../api/tollLocations";
import type { TollLocation } from "../../types/domain";
import { PageHeader } from "../../components/common/PageHeader";
import { QueryError } from "../../components/common/QueryError";
import { formatMoney, en } from "../../lib/format";
import { ExportMenu } from "../../components/common/ExportMenu";
import { type ExportField } from "../../lib/export";
import { errorMessage } from "../../lib/axios";
import {
  TollLocationFormModal,
  type TollLocationFormValues,
} from "./TollLocationFormModal";

const VEHICLE_LABEL: Record<string, string> = {
  car: "Car",
  motorcycle: "Motorcycle",
  tricycle: "Tricycle",
};

const exportFields: ExportField<TollLocation>[] = [
  { label: "Name", value: (t) => en(t.name) },
  { label: "Fee (TZS)", value: (t) => t.feeTZS ?? 0 },
  { label: "Radius (m)", value: (t) => t.radiusMeters ?? 0 },
  { label: "Latitude", value: (t) => t.center?.coordinates?.[1] ?? "" },
  { label: "Longitude", value: (t) => t.center?.coordinates?.[0] ?? "" },
  {
    label: "Vehicle types",
    value: (t) => (t.appliesToVehicleTypes ?? []).join(", "),
  },
  { label: "Active", value: (t) => (t.isActive ? "Yes" : "No") },
];

// Convert the form's flat lat/lng into the GeoJSON body the API expects.
function toBody(values: TollLocationFormValues): Partial<TollLocation> {
  return {
    name: values.name,
    center: {
      type: "Point",
      coordinates: [values.longitude, values.latitude],
    },
    radiusMeters: values.radiusMeters,
    feeTZS: values.feeTZS,
    appliesToVehicleTypes: values.appliesToVehicleTypes,
    isActive: values.isActive,
  };
}

export function TollLocationsPage() {
  const qc = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TollLocation | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["toll-locations"],
    queryFn: () => tollLocationsApi.list(),
  });

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ["toll-locations"] });
  }

  const saveMutation = useMutation({
    mutationFn: (values: TollLocationFormValues) =>
      editing
        ? tollLocationsApi.update(editing._id, toBody(values))
        : tollLocationsApi.create(toBody(values)),
    onSuccess: () => {
      notifications.show({
        color: "green",
        message: editing ? "Toll location updated" : "Toll location created",
      });
      setFormOpen(false);
      setEditing(null);
      invalidate();
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => tollLocationsApi.remove(id),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Toll location deleted" });
      invalidate();
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  function confirmDelete(t: TollLocation) {
    modals.openConfirmModal({
      title: "Delete toll location",
      children: (
        <Text size="sm">
          Delete <b>{en(t.name)}</b>? Existing trips keep their charged tolls;
          new trips will no longer be charged here.
        </Text>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: () => deleteMutation.mutate(t._id),
    });
  }

  return (
    <>
      <PageHeader
        title="Toll Locations"
        subtitle="Location-based toll fees added automatically to matching trips"
        actions={
          <Group gap="sm">
            <ExportMenu
              filename="toll-locations"
              fields={exportFields}
              disabled={isLoading}
              fetchAll={async () => data?.items ?? []}
            />
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              New toll location
            </Button>
          </Group>
        }
      />

      {error && (
        <Box mb="lg">
          <QueryError error={error} onRetry={refetch} />
        </Box>
      )}

      {isLoading ? (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} h={180} radius="md" />
          ))}
        </SimpleGrid>
      ) : (data?.items ?? []).length === 0 ? (
        <Text c="dimmed" ta="center" mt="xl">
          No toll locations yet. Create one to start charging location-based
          fees.
        </Text>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {(data?.items ?? []).map((t) => (
            <Card key={t._id} withBorder radius="md" padding="lg">
              <Group justify="space-between" align="flex-start" mb="sm">
                <Stack gap={2}>
                  <Text fw={600}>{en(t.name)}</Text>
                  <Badge
                    size="sm"
                    variant="light"
                    color={t.isActive ? "green" : "gray"}
                  >
                    {t.isActive ? "Active" : "Inactive"}
                  </Badge>
                </Stack>
                <Menu position="bottom-end" withArrow>
                  <Menu.Target>
                    <ActionIcon variant="subtle" color="gray">
                      <IconDotsVertical size={18} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconEdit size={16} />}
                      onClick={() => {
                        setEditing(t);
                        setFormOpen(true);
                      }}
                    >
                      Edit
                    </Menu.Item>
                    <Menu.Item
                      color="red"
                      leftSection={<IconTrash size={16} />}
                      onClick={() => confirmDelete(t)}
                    >
                      Delete
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>

              <Stack gap={4}>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Fee
                  </Text>
                  <Text size="sm" fw={600}>
                    {formatMoney(t.feeTZS)}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Radius
                  </Text>
                  <Text size="sm">{t.radiusMeters ?? "—"} m</Text>
                </Group>
                <Group justify="space-between" align="flex-start">
                  <Text size="sm" c="dimmed">
                    Applies to
                  </Text>
                  <Group gap={4} justify="flex-end">
                    {(t.appliesToVehicleTypes ?? []).map((vt) => (
                      <Badge key={vt} size="xs" variant="outline">
                        {VEHICLE_LABEL[vt] ?? vt}
                      </Badge>
                    ))}
                  </Group>
                </Group>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      )}

      <TollLocationFormModal
        opened={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        editing={editing}
        loading={saveMutation.isPending}
        onSubmit={(values) => saveMutation.mutate(values)}
      />
    </>
  );
}
